import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  booleanAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type DsCardVariant = 'elevated-l1' | 'elevated-l2' | 'elevated-l3' | 'outlined' | 'filled';
export type DsCardShadow = 'none' | 'l1' | 'l2' | 'l3';
export type DsCardBackgroundBlur = 'none' | 'l1' | 'l2' | 'l3';
export type DsCardBackgroundOpacity = 'opaque' | 'subtle' | 'medium' | 'strong';

@Component({
  selector: 'ds-card',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-card"
      [class]="hostClasses"
      [style.--ds-card-border-color]="borderColor"
      [style.--ds-card-resolved-gap]="gap"
      [style.--ds-card-custom-background-opacity]="customBackgroundOpacity"
      [style.--ds-card-gradient-border-angle]="gradientBorderAngle"
      [attr.role]="isInteractive ? 'button' : null"
      [attr.tabindex]="isInteractive ? 0 : null"
      (click)="onHostActivate($event)"
      (keydown.enter)="onHostActivate($event)"
      (keydown.space)="onHostActivate($event)"
    >
      <span
        class="ds-card__background"
        [style.--ds-card-base-background]="resolvedBackground"
        aria-hidden="true"
      ></span>
      <div class="ds-card__content">
        <ng-content />
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    .ds-card {
      --ds-card-resolved-border-radius: var(--ds-card-border-radius);
      border-radius:  var(--ds-card-resolved-border-radius);
      padding:        var(--ds-card-padding);
      display:        block;
      box-sizing:     border-box;
      position:       relative;
      overflow:       hidden;
      backdrop-filter: blur(var(--ds-card-resolved-background-blur, 0));
      -webkit-backdrop-filter: blur(var(--ds-card-resolved-background-blur, 0));
      transition:
        box-shadow 300ms ease,
        transform var(--ds-card-hover-transition-duration) ease,
        backdrop-filter 300ms ease,
        -webkit-backdrop-filter 300ms ease;
      transform-origin: center;
      color:          var(--ds-text-primary);
    }

    .ds-card--full-height {
      height: 100%;
    }

    .ds-card--full-height .ds-card__content {
      height: 100%;
    }

    .ds-card__background {
      position:       absolute;
      inset:          0;
      z-index:        0;
      border-radius:  inherit;
      background:     var(--ds-card-base-background);
      opacity:        var(--ds-card-resolved-background-opacity, 1);
      pointer-events: none;
      transition:
        background 300ms ease,
        opacity 300ms ease;
    }

    .ds-card__content {
      position:       relative;
      z-index:        2;
      display:        flex;
      flex-direction: column;
      gap:            var(--ds-card-resolved-gap, var(--ds-card-gap));
      width:          100%;
    }

    .ds-card--gradient-border::before {
      content:        "";
      position:       absolute;
      inset:          0;
      z-index:        0;
      border-radius:  inherit;
      background:     conic-gradient(
        from var(--ds-card-gradient-border-angle, 0deg),
        var(--ds-foundation-colors-color-brand-blue-500),
        var(--ds-foundation-colors-color-brand-yellow-200),
        var(--ds-foundation-colors-color-brand-aqua-200),
        var(--ds-foundation-colors-color-brand-blue-500)
      );
      pointer-events: none;
      animation: ds-card-gradient-border-spin 3000ms cubic-bezier(0.45, 0, 0.55, 1) infinite;
    }

    .ds-card--gradient-border .ds-card__background {
      inset:         var(--ds-card-gradient-border-width);
      border-radius: max(0px, calc(var(--ds-card-resolved-border-radius) - 1px));
    }

    @property --ds-card-gradient-border-angle {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }

    @keyframes ds-card-gradient-border-spin {
      to {
        --ds-card-gradient-border-angle: 360deg;
      }
    }

    .ds-card--interactive:hover .ds-card__background,
    .ds-card--interactive:active .ds-card__background {
      background:
        linear-gradient(var(--ds-card-hover-overlay), var(--ds-card-hover-overlay)),
        var(--ds-card-base-background);
      background-blend-mode: multiply;
    }

    .ds-card--interactive:hover,
    .ds-card--interactive:active {
      transform: scale(var(--ds-card-hover-scale));
    }

    /* ── Variants ──────────────────────────────────────────── */
    .ds-card--shadow-l1,
    .ds-card--elevated-l1 {
      box-shadow: var(--ds-shadow-l1);
    }

    .ds-card--shadow-l2,
    .ds-card--elevated-l2 {
      box-shadow: var(--ds-shadow-l2);
    }

    .ds-card--shadow-l3,
    .ds-card--elevated-l3 {
      box-shadow: var(--ds-shadow-l3);
    }

    .ds-card--shadow-none {
      box-shadow: none;
    }

    .ds-card--background-blur-l1 {
      --ds-card-resolved-background-blur: var(--ds-card-background-blur-l1);
    }

    .ds-card--background-blur-l2 {
      --ds-card-resolved-background-blur: var(--ds-card-background-blur-l2);
    }

    .ds-card--background-blur-l3 {
      --ds-card-resolved-background-blur: var(--ds-card-background-blur-l3);
    }

    .ds-card--background-opacity-subtle {
      --ds-card-resolved-background-opacity: calc(var(--ds-card-background-opacity-subtle) / 100px);
    }

    .ds-card--background-opacity-medium {
      --ds-card-resolved-background-opacity: calc(var(--ds-card-background-opacity-medium) / 100px);
    }

    .ds-card--background-opacity-strong {
      --ds-card-resolved-background-opacity: calc(var(--ds-card-background-opacity-strong) / 100px);
    }

    .ds-card--background-opacity-custom {
      --ds-card-resolved-background-opacity: var(--ds-card-custom-background-opacity);
    }

    .ds-card--bordered,
    .ds-card--outlined {
      border: var(--ds-card-border-width) solid var(--ds-card-border-color);
    }

    .ds-card--borderless {
      border: 0;
    }

    /* ── Interactive ──────────────────────────────────────── */
    .ds-card--interactive {
      cursor: pointer;
      outline: none;
    }

    /* ── Focus ring (mismo estilo que inputs) ──────────────── */
    .ds-card--interactive:focus-visible {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }
  `],
})
export class DsCardComponent {
  private static readonly NESTED_INTERACTIVE_SELECTOR = 'a[href], button, input, textarea, select, summary';

  @Input() background = 'var(--ds-card-elevated-background)';
  @Input() gap = 'var(--ds-card-gap)';
  @Input() shadow: DsCardShadow = 'l1';
  @Input() backgroundBlur: DsCardBackgroundBlur = 'none';
  @Input() backgroundOpacity: DsCardBackgroundOpacity = 'opaque';
  @Input() customBackgroundOpacity: number | string | null = null;
  @Input() border: boolean | string = false;
  @Input() borderColor = 'var(--ds-card-outlined-border-color)';
  @Input() gradientBorder: boolean | string = false;
  @Input() interactive: boolean | string | null = null;
  @Input({ transform: booleanAttribute }) fullHeight = false;

  @Output() dsCardClick = new EventEmitter<Event>();

  @Input() set variant(value: DsCardVariant | undefined) {
    if (!value) return;

    const presets: Record<DsCardVariant, { background: string; shadow: DsCardShadow; border: boolean }> = {
      'elevated-l1': { background: 'var(--ds-card-elevated-background)', shadow: 'l1', border: false },
      'elevated-l2': { background: 'var(--ds-card-elevated-background)', shadow: 'l2', border: false },
      'elevated-l3': { background: 'var(--ds-card-elevated-background)', shadow: 'l3', border: false },
      outlined: { background: 'var(--ds-card-outlined-background)', shadow: 'none', border: true },
      filled: { background: 'var(--ds-card-filled-background)', shadow: 'none', border: false },
    };
    const preset = presets[value];

    this.background = preset.background;
    this.shadow = preset.shadow;
    this.border = preset.border;
  }

  get resolvedBackground(): string {
    return this.background;
  }

  get hasBorder(): boolean {
    return this.border === true || this.border === '' || this.border === 'true';
  }

  get hasGradientBorder(): boolean {
    return this.gradientBorder === true || this.gradientBorder === '' || this.gradientBorder === 'true';
  }

  get gradientBorderAngle(): string {
    return this.hasGradientBorder ? '0deg' : '';
  }

  onHostActivate(event: Event): void {
    if (!this.isInteractive || this.isFromNestedInteractiveElement(event)) return;
    this.dsCardClick.emit(event);
  }

  private isFromNestedInteractiveElement(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest(DsCardComponent.NESTED_INTERACTIVE_SELECTOR);
  }

  get isInteractive(): boolean {
    if (this.interactive === null || this.interactive === undefined) {
      return this.dsCardClick.observers.length > 0;
    }

    return this.interactive === true || this.interactive === '' || this.interactive === 'true';
  }

  get hostClasses(): string {
    return [
      'ds-card',
      `ds-card--shadow-${this.shadow}`,
      `ds-card--background-blur-${this.backgroundBlur}`,
      `ds-card--background-opacity-${this.resolvedBackgroundOpacity}`,
      this.hasBorder ? 'ds-card--bordered' : 'ds-card--borderless',
      this.hasGradientBorder ? 'ds-card--gradient-border' : '',
      this.fullHeight ? 'ds-card--full-height' : '',
      this.isInteractive ? 'ds-card--interactive' : '',
    ].filter(Boolean).join(' ');
  }

  private get resolvedBackgroundOpacity(): DsCardBackgroundOpacity | 'custom' {
    return this.customBackgroundOpacity === null || this.customBackgroundOpacity === undefined || this.customBackgroundOpacity === ''
      ? this.backgroundOpacity
      : 'custom';
  }
}
